import { IncorporationRequestDataType } from '@commenda/types';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { EDUCATIONAL_QUALIFICATION_TYPE_ENUM_TO_TEXT } from '@commenda/utils';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { LegacyDocumentType, WorkRequestType } from '@prisma/client';
import { Country, State } from 'country-state-city';
import { SummaryFieldKS } from '../../common';
import { DocumentSummaryField } from '../../common/DocumentSummaryField';
import { getDirectorName } from '../../Director/common';
import { getIdentityProof } from '../utils';

const INPVTLTDFounderField = ({
  director,
  data,
  workRequestType,
  showDocuments,
}: {
  director: DirectorBase;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  workRequestType?: WorkRequestType;
  showDocuments?: boolean;
}) => {
  const identityProof = getIdentityProof(director, data.corporationId);
  const passportSizePhotoFile = director?.directorFiles
    ?.sort((a, b) => b.id - a.id)
    .find(
      (eachFile) =>
        eachFile.documentType === LegacyDocumentType.PassportSizePhoto
    );

  const specimenSignatureFile = director?.directorFiles
    ?.sort((a, b) => b.id - a.id)
    .find(
      (eachFile) =>
        eachFile.documentType === LegacyDocumentType.SpecimenSignature
    );
  const officers = director?.corporationHasDirector
    ?.find(
      (dirWithCorp) =>
        dirWithCorp.directorId === director.id &&
        dirWithCorp.corporationId === data.corporationId
    )
    ?.additionalDirectorInformation?.boardPosition?.join(', ');

  const directorVsEquityMap: Record<
    number,
    { sharePercentage: number; name: string; id: number }
  > = {};

  data?.directors?.forEach((dir) => {
    const details = dir.corporationHasDirector.find(
      (directorWithCorporation) =>
        directorWithCorporation.corporationId === data.corporationId
    )?.additionalDirectorInformation;

    directorVsEquityMap[dir.id] = {
      name: getDirectorName(dir),
      sharePercentage: details?.sharePercentage,
      id: dir.id,
    };
  });

  const physicalAddress = director?.operatingAddress;

  const sharePercentage = director?.corporationHasDirector?.find(
    (dirWithCorp) =>
      dirWithCorp.directorId === director.id &&
      dirWithCorp.corporationId === data.corporationId
  )?.additionalDirectorInformation?.sharePercentage;

  const openFile = async (fileId: number) => {
    const res = await getSignedUrlByFileId(fileId, null);
    window.open(res);
  };

  return (
    <div className="p-6 flex flex-col gap-2 bg-bg-disabled-subtle">
      <SummaryFieldKS 
        heading="First Name" 
        value={director?.directorFirstName || '-'} 
      />
      <SummaryFieldKS 
        heading="Last Name" 
        value={director?.directorLastName || '-'} 
      />
      
      <SummaryFieldKS heading="Phone Number" value={director?.number} />
      <SummaryFieldKS heading="Email" value={director?.email} />

      {director?.dateOfBirth && (
        <SummaryFieldKS heading="Date of Birth" value={director.dateOfBirth} />
      )}

      <SummaryFieldKS
        heading="Has Digital Signature Certificate ?"
        value={director?.personalDetails?.hasDSC ? 'Yes' : 'No'}
      />
      {director?.directorIdentificationNumber ? (
        <SummaryFieldKS
          heading="DIN"
          value={director.directorIdentificationNumber}
        />
      ) : null}
      {director?.personalDetails?.nationality ? (
        <SummaryFieldKS
          heading="Nationality"
          value={
            Country.getCountryByCode(director?.personalDetails?.nationality)
              ?.name
          }
        />
      ) : null}

      {director?.personalDetails?.residence && (
        <SummaryFieldKS
          heading="Country of Residence"
          value={
            Country.getCountryByCode(director?.personalDetails?.residence)?.name
          }
        />
      )}

      {director.personalDetails?.educationalQualification ? (
        <SummaryFieldKS
          heading="Education Qualification"
          value={
            EDUCATIONAL_QUALIFICATION_TYPE_ENUM_TO_TEXT[
              director?.personalDetails?.educationalQualification
            ]
          }
        />
      ) : null}
      {director.personalDetails?.occupation ? (
        <SummaryFieldKS
          heading="Occupation"
          value={director.personalDetails?.occupation}
        />
      ) : null}
      {director.personalDetails?.placeOfBirth ? (
        <SummaryFieldKS
          heading="Place of Birth"
          value={`${director?.personalDetails?.placeOfBirth?.district}, ${State.getStateByCodeAndCountry(director?.personalDetails?.placeOfBirth?.state, director?.personalDetails?.placeOfBirth?.country)?.name},  ${Country.getCountryByCode(director?.personalDetails?.placeOfBirth?.country)?.name}`}
        />
      ) : null}
      <SummaryFieldKS
        heading="Current Address"
        value={[
          `${physicalAddress?.addressLine1}, ${physicalAddress?.addressLine2}`,
          `${physicalAddress?.city} - ${physicalAddress?.postalCode}`,
          `${
            State.getStateByCodeAndCountry(
              physicalAddress?.state,
              physicalAddress?.country
            )?.name
          }, ${Country.getCountryByCode(physicalAddress?.country)?.name}`,
        ]}
      />
      {director.personalDetails?.dateOfOccupancyInCurrentAddress ? (
        <SummaryFieldKS
          heading="Date of Occupancy in Current Address"
          value={director.personalDetails?.dateOfOccupancyInCurrentAddress}
        />
      ) : null}

      {officers ? (
        <SummaryFieldKS heading="Roles" value={officers || 'No'} />
      ) : null}

      {showDocuments && director?.addressProof?.fileUrl ? (
        <DocumentSummaryField
          onClick={() => openFile(director?.addressProof?.id)}
          title="Proof of Address"
          className="grid grid-cols-2 gap-8"
        />
      ) : null}

      {showDocuments && director?.aadharCard?.fileUrl ? (
        <DocumentSummaryField
          onClick={() => openFile(director?.aadharCard?.id)}
          title="Aadhar Card"
          className="grid grid-cols-2 gap-8"
        />
      ) : null}

      {showDocuments && director?.panCard?.fileUrl ? (
        <DocumentSummaryField
          onClick={() => openFile(director?.panCard?.id)}
          title="Pan Card"
          className="grid grid-cols-2 gap-8"
        />
      ) : null}
      {showDocuments && identityProof?.fileUrl ? (
        <DocumentSummaryField
          onClick={() => openFile(identityProof?.id)}
          title="Identity Proof"
          className="grid grid-cols-2 gap-8"
        />
      ) : null}
      {showDocuments && passportSizePhotoFile?.fileUrl ? (
        <DocumentSummaryField
          onClick={() => openFile(passportSizePhotoFile?.id)}
          title="Passport Size Photo"
          className="grid grid-cols-2 gap-8"
        />
      ) : null}
      {showDocuments && specimenSignatureFile?.fileUrl ? (
        <DocumentSummaryField
          onClick={() => openFile(specimenSignatureFile?.id)}
          title="Specimen Signature"
          className="grid grid-cols-2 gap-8"
        />
      ) : null}
    </div>
  );
};

export default INPVTLTDFounderField;
